import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import { DollarSign, RefreshCw, Receipt } from 'lucide-react';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ezerpToken')}`
});

const componentTypeColors = {
  earning: 'bg-green-500/20 text-green-400',
  deduction: 'bg-red-500/20 text-red-400',
  employer_cost: 'bg-blue-500/20 text-blue-400',
  tax: 'bg-amber-500/20 text-amber-400'
};

export const PayComponentsPage = () => {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchComponents = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/hr/pay-components`;
      if (typeFilter !== 'all') url += `?component_type=${encodeURIComponent(typeFilter)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch pay components');
      const data = await res.json();
      setComponents(Array.isArray(data) ? data : data.components || []);
    } catch (err) {
      toast.error(err.message);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchComponents();
  }, [fetchComponents]);

  const totals = useMemo(() => ({
    all: components.length,
    earnings: components.filter((item) => item.component_type === 'earning').length,
    deductions: components.filter((item) => item.component_type === 'deduction').length,
    taxes: components.filter((item) => item.component_type === 'tax').length
  }), [components]);

  return (
    <div className="space-y-6" data-testid="pay-components-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">Pay Components</h1>
          <p className="text-muted-foreground mt-1">Review payroll earnings, deductions, taxes, and employer costs.</p>
        </div>
        <Button variant="outline" onClick={fetchComponents} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-muted-foreground">Total Components</p><p className="text-3xl font-bold">{totals.all}</p></div>
              <div className="p-3 rounded-lg bg-green-500/20"><Receipt className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Earnings</p>
            <p className="text-3xl font-bold">{totals.earnings}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Deductions</p>
            <p className="text-3xl font-bold">{totals.deductions}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Taxes</p>
            <p className="text-3xl font-bold">{totals.taxes}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Component Registry</CardTitle>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="Component type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="earning">Earnings</SelectItem>
                <SelectItem value="deduction">Deductions</SelectItem>
                <SelectItem value="employer_cost">Employer Costs</SelectItem>
                <SelectItem value="tax">Taxes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div>
          ) : components.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">No pay components found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-left p-3 font-medium">Taxable</th>
                    <th className="text-left p-3 font-medium">Order</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {components.map((item) => (
                    <tr key={item.id || item.code} className="hover:bg-slate-800/30">
                      <td className="p-3 font-mono text-sm">{item.code || '-'}</td>
                      <td className="p-3 font-medium">{item.name || '-'}</td>
                      <td className="p-3"><Badge className={componentTypeColors[item.component_type] || 'bg-gray-500/20 text-gray-400'}>{item.component_type || 'unknown'}</Badge></td>
                      <td className="p-3">{item.is_taxable ? 'Yes' : 'No'}</td>
                      <td className="p-3">{item.display_order ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayComponentsPage;
